import { useEffect, useState } from "react";
import { api } from "../api";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Plus, Trash2, Pencil, X, Sparkles } from "lucide-react";
import AIPanel from "./AIPanel";

const TABS = ["dashboard", "portfolio", "inquiries", "insights", "ai"];

const emptyCompany = {
  name: "", sector: "", region: "", year_acquired: new Date().getFullYear(),
  revenue_eur_m: 0, employees: 0, description_it: "", description_en: "",
  website: "", logo_url: "", image_url: "", featured: false,
};

const emptyInsight = {
  slug: "", title_it: "", title_en: "", excerpt_it: "", excerpt_en: "",
  body_it: "", body_en: "", cover_url: "", category: "insight",
  tags: [], region_tags: [], reading_minutes: 7,
};

const REGION_OPTIONS = ["lombardia", "veneto", "emilia-romagna", "piemonte", "toscana"];
const TAG_OPTIONS = ["valutazione", "successione", "fisco", "governance", "metodo", "banche", "sale-ready", "multipli", "distretti", "meccanica", "alimentare"];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const { t } = useI18n();
  const nav = useNavigate();
  const [tab, setTab] = useState("dashboard");

  const [companies, setCompanies] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [insights, setInsights] = useState([]);
  const [stats, setStats] = useState({});
  const [aiUsage, setAiUsage] = useState(null);

  const [editingCompany, setEditingCompany] = useState(null);
  const [editingInsight, setEditingInsight] = useState(null);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiDraftForm, setAiDraftForm] = useState(null); // null | { topic, angle, tags, region_tags, category }

  const load = async () => {
    const [p, i, ins, s] = await Promise.all([
      api.get("/portfolio"),
      api.get("/admin/inquiries"),
      api.get("/insights"),
      api.get("/stats"),
    ]);
    setCompanies(p.data);
    setInquiries(i.data);
    setInsights(ins.data);
    setStats(s.data);
    try {
      const u = await api.get("/ai/usage");
      setAiUsage(u.data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const doLogout = async () => {
    await logout();
    nav("/admin/login");
  };

  const saveCompany = async () => {
    const payload = { ...editingCompany };
    delete payload.id;
    delete payload.created_at;
    payload.revenue_eur_m = parseFloat(payload.revenue_eur_m) || 0;
    payload.employees = parseInt(payload.employees) || 0;
    payload.year_acquired = parseInt(payload.year_acquired) || new Date().getFullYear();
    if (editingCompany.id) {
      await api.put(`/admin/portfolio/${editingCompany.id}`, payload);
    } else {
      await api.post("/admin/portfolio", payload);
    }
    setEditingCompany(null);
    await load();
  };

  const deleteCompany = async (id) => {
    if (!window.confirm("Confermare eliminazione?")) return;
    await api.delete(`/admin/portfolio/${id}`);
    await load();
  };

  const saveInsight = async () => {
    const payload = { ...editingInsight };
    delete payload.id;
    delete payload.published_at;
    delete payload.author;
    delete payload._fromAi;
    if (editingInsight.id) {
      await api.put(`/admin/insights/${editingInsight.id}`, payload);
    } else {
      await api.post("/admin/insights", payload);
    }
    setEditingInsight(null);
    await load();
  };

  const deleteInsight = async (id) => {
    if (!window.confirm("Confermare eliminazione?")) return;
    await api.delete(`/admin/insights/${id}`);
    await load();
  };

  const generateAiDraft = async () => {
    if (!aiDraftForm?.topic?.trim()) return;
    setGeneratingAi(true);
    try {
      const r = await api.post("/admin/insights/generate-draft", aiDraftForm);
      const d = r.data.draft;
      // La bozza AI diventa un editingInsight subito modificabile prima del salvataggio
      setEditingInsight({
        slug: d.slug || "",
        title_it: d.title_it || "",
        title_en: d.title_en || "",
        excerpt_it: d.excerpt_it || "",
        excerpt_en: d.excerpt_en || "",
        body_it: d.body_it || "",
        body_en: d.body_en || "",
        cover_url: "",
        category: d.category || aiDraftForm.category || "insight",
        tags: d.tags || [],
        region_tags: d.region_tags || [],
        reading_minutes: d.reading_minutes || 7,
        _fromAi: true,
      });
      setAiDraftForm(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Errore nella generazione bozza. Riprova.";
      window.alert(msg);
    } finally {
      setGeneratingAi(false);
    }
  };

  const updateInquiryStatus = async (id, status) => {
    await api.patch(`/admin/inquiries/${id}`, { status });
    await load();
  };

  const deleteInquiry = async (id) => {
    if (!window.confirm("Confermare eliminazione?")) return;
    await api.delete(`/admin/inquiries/${id}`);
    await load();
  };

  return (
    <div className="admin-shell" data-testid="admin-dashboard">
      <header className="border-b" style={{ background: "#fff", borderColor: "#E2E8F0" }}>
        <div className="max-w-[1400px] mx-auto px-6 flex items-center justify-between" style={{ height: 64 }}>
          <div className="flex items-center gap-8">
            <Link to="/" className="serif-display" style={{ fontSize: 26, color: "#0F172A" }}>GIANO</Link>
            <nav className="flex gap-1">
              {TABS.map((tb) => (
                <button
                  key={tb}
                  onClick={() => setTab(tb)}
                  data-testid={`tab-${tb}`}
                  className="px-4 py-2 text-sm font-medium transition-colors"
                  style={{
                    color: tab === tb ? "#0F172A" : "#64748B",
                    borderBottom: tab === tb ? "2px solid #0F172A" : "2px solid transparent",
                  }}
                >
                  {t(`admin.${tb}`)}
                </button>
              ))}
            </nav>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm" style={{ color: "#64748B" }}>{user?.email}</span>
            <button onClick={doLogout} className="btn-ghost" style={{ padding: "0.5rem 1rem", fontSize: 13 }} data-testid="admin-logout">
              <LogOut size={14} /> {t("admin.logout")}
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-6 py-10">
        {tab === "dashboard" && (
          <div>
            <h1 className="serif-display" style={{ fontSize: 40 }}>{t("admin.dashboard")}</h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
              {[
                { label: "Aziende", value: stats.companies || 0 },
                { label: "Dipendenti", value: (stats.employees || 0).toLocaleString("it-IT") },
                { label: "Ricavi (€M)", value: (stats.aggregate_revenue_eur_m || 0).toFixed(1) },
                { label: "Richieste nuove", value: inquiries.filter((i) => i.status === "new").length },
              ].map((k) => (
                <div key={k.label} className="admin-card p-6">
                  <div className="overline" style={{ color: "#64748B" }}>{k.label}</div>
                  <div className="serif-display mt-2" style={{ fontSize: 40 }}>{k.value}</div>
                </div>
              ))}
            </div>

            {aiUsage && aiUsage.total_calls > 0 && (
              <div className="admin-card mt-8 p-6" data-testid="ai-usage-card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="serif-display" style={{ fontSize: 22 }}>GIANO Analytics · Consumo</h2>
                  <span className="font-mono text-xs" style={{ color: "#64748B" }}>{aiUsage.total_calls} chiamate totali</span>
                </div>
                <table className="admin-table">
                  <thead><tr><th>Tipo</th><th>Modalità</th><th>Chiamate</th><th>Durata media</th><th>Tempo totale</th></tr></thead>
                  <tbody>
                    {aiUsage.by_kind.map((row, i) => (
                      <tr key={i}>
                        <td>{row.kind}</td>
                        <td className="font-mono text-xs">{row.mode}</td>
                        <td className="font-mono text-xs">{row.count}</td>
                        <td className="font-mono text-xs">{row.avg_sec}s</td>
                        <td className="font-mono text-xs">{row.total_sec}s</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <h2 className="serif-display mt-12" style={{ fontSize: 24 }}>Ultime richieste</h2>
            <div className="admin-card mt-4 overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Data</th><th>Nome</th><th>Azienda</th><th>Ricavi</th><th>Status</th></tr></thead>
                <tbody>
                  {inquiries.slice(0, 5).map((iq) => (
                    <tr key={iq.id}>
                      <td className="font-mono text-xs">{new Date(iq.created_at).toLocaleDateString("it-IT")}</td>
                      <td>{iq.full_name}</td>
                      <td>{iq.company}</td>
                      <td className="font-mono text-xs">{iq.revenue_range || "—"}</td>
                      <td><span className={`status-pill status-${iq.status}`}>{iq.status}</span></td>
                    </tr>
                  ))}
                  {inquiries.length === 0 && <tr><td colSpan={5} style={{ color: "#64748B", textAlign: "center", padding: 24 }}>Nessuna richiesta</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "portfolio" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="serif-display" style={{ fontSize: 32 }}>{t("admin.portfolio")}</h1>
              <button className="btn-primary" onClick={() => setEditingCompany({ ...emptyCompany })} data-testid="new-company-btn">
                <Plus size={14} /> {t("admin.new")}
              </button>
            </div>
            <div className="admin-card overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Nome</th><th>Settore</th><th>Regione</th><th>Anno</th><th>Ricavi €M</th><th>Dip.</th><th></th></tr></thead>
                <tbody>
                  {companies.map((c) => (
                    <tr key={c.id} data-testid={`company-row-${c.id}`}>
                      <td style={{ fontWeight: 500 }}>{c.name}</td>
                      <td>{c.sector}</td>
                      <td>{c.region}</td>
                      <td className="font-mono text-xs">{c.year_acquired}</td>
                      <td className="font-mono text-xs">{c.revenue_eur_m.toFixed(1)}</td>
                      <td className="font-mono text-xs">{c.employees}</td>
                      <td>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingCompany(c)} data-testid={`edit-company-${c.id}`} className="text-slate-600 hover:text-slate-900"><Pencil size={16} /></button>
                          <button onClick={() => deleteCompany(c.id)} data-testid={`delete-company-${c.id}`} className="text-slate-600 hover:text-red-600"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "inquiries" && (
          <div>
            <h1 className="serif-display" style={{ fontSize: 32 }}>{t("admin.inquiries")}</h1>
            <div className="admin-card mt-6 overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Data</th><th>Nome</th><th>Email</th><th>Azienda</th><th>Ricavi</th><th>Regione</th><th>Status</th><th></th></tr></thead>
                <tbody>
                  {inquiries.map((iq) => (
                    <tr key={iq.id} data-testid={`inquiry-row-${iq.id}`}>
                      <td className="font-mono text-xs">{new Date(iq.created_at).toLocaleDateString("it-IT")}</td>
                      <td>{iq.full_name}</td>
                      <td className="text-xs">{iq.email}</td>
                      <td>{iq.company}</td>
                      <td className="font-mono text-xs">{iq.revenue_range || "—"}</td>
                      <td>{iq.region || "—"}</td>
                      <td>
                        <select value={iq.status} onChange={(e) => updateInquiryStatus(iq.id, e.target.value)} className="admin-input" style={{ padding: 4, fontSize: 12 }}>
                          <option value="new">new</option>
                          <option value="contacted">contacted</option>
                          <option value="qualified">qualified</option>
                          <option value="archived">archived</option>
                        </select>
                      </td>
                      <td><button onClick={() => deleteInquiry(iq.id)} className="text-slate-600 hover:text-red-600"><Trash2 size={16} /></button></td>
                    </tr>
                  ))}
                  {inquiries.length === 0 && <tr><td colSpan={8} style={{ color: "#64748B", textAlign: "center", padding: 24 }}>Nessuna richiesta</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "insights" && (
          <div>
            <div className="flex items-center justify-between mb-6">
              <h1 className="serif-display" style={{ fontSize: 32 }}>{t("admin.insights")}</h1>
              <div className="flex gap-2">
                <button
                  className="btn-ghost"
                  onClick={() => setAiDraftForm({ topic: "", angle: "", tags: [], region_tags: [], category: "insight", target_length_words: 1000 })}
                  data-testid="ai-draft-btn"
                  style={{ borderColor: "var(--terracotta)", color: "var(--terracotta)" }}
                >
                  <Sparkles size={14} /> Bozza AI
                </button>
                <button className="btn-primary" onClick={() => setEditingInsight({ ...emptyInsight })} data-testid="new-insight-btn">
                  <Plus size={14} /> {t("admin.new")}
                </button>
              </div>
            </div>
            <div className="admin-card overflow-x-auto">
              <table className="admin-table">
                <thead><tr><th>Titolo (IT)</th><th>Slug</th><th>Categoria</th><th>Pubblicato</th><th></th></tr></thead>
                <tbody>
                  {insights.map((it) => (
                    <tr key={it.id}>
                      <td style={{ fontWeight: 500 }}>{it.title_it}</td>
                      <td className="font-mono text-xs">{it.slug}</td>
                      <td>{it.category}</td>
                      <td className="font-mono text-xs">{new Date(it.published_at).toLocaleDateString("it-IT")}</td>
                      <td>
                        <div className="flex gap-2">
                          <button onClick={() => setEditingInsight(it)}><Pencil size={16} /></button>
                          <button onClick={() => deleteInsight(it.id)} className="hover:text-red-600"><Trash2 size={16} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "ai" && <AIPanel inquiries={inquiries} />}
      </main>

      {editingCompany && (
        <Modal title={editingCompany.id ? "Modifica azienda" : "Nuova azienda"} onClose={() => setEditingCompany(null)} onSave={saveCompany}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Nome"><input className="admin-input" value={editingCompany.name} onChange={(e) => setEditingCompany({ ...editingCompany, name: e.target.value })} data-testid="company-form-name" /></Field>
            <Field label="Settore"><input className="admin-input" value={editingCompany.sector} onChange={(e) => setEditingCompany({ ...editingCompany, sector: e.target.value })} /></Field>
            <Field label="Regione"><input className="admin-input" value={editingCompany.region} onChange={(e) => setEditingCompany({ ...editingCompany, region: e.target.value })} /></Field>
            <Field label="Anno acquisizione"><input type="number" className="admin-input" value={editingCompany.year_acquired} onChange={(e) => setEditingCompany({ ...editingCompany, year_acquired: e.target.value })} /></Field>
            <Field label="Ricavi (€M)"><input type="number" step="0.1" className="admin-input" value={editingCompany.revenue_eur_m} onChange={(e) => setEditingCompany({ ...editingCompany, revenue_eur_m: e.target.value })} /></Field>
            <Field label="Dipendenti"><input type="number" className="admin-input" value={editingCompany.employees} onChange={(e) => setEditingCompany({ ...editingCompany, employees: e.target.value })} /></Field>
            <Field label="URL immagine" full><input className="admin-input" value={editingCompany.image_url} onChange={(e) => setEditingCompany({ ...editingCompany, image_url: e.target.value })} /></Field>
            <Field label="Descrizione IT" full><textarea rows={3} className="admin-input" value={editingCompany.description_it} onChange={(e) => setEditingCompany({ ...editingCompany, description_it: e.target.value })} /></Field>
            <Field label="Descrizione EN" full><textarea rows={3} className="admin-input" value={editingCompany.description_en} onChange={(e) => setEditingCompany({ ...editingCompany, description_en: e.target.value })} /></Field>
          </div>
        </Modal>
      )}

      {editingInsight && (
        <Modal title={editingInsight.id ? "Modifica insight" : (editingInsight._fromAi ? "Bozza AI · rivedi e salva" : "Nuovo insight")} onClose={() => setEditingInsight(null)} onSave={saveInsight}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Slug"><input className="admin-input" value={editingInsight.slug} onChange={(e) => setEditingInsight({ ...editingInsight, slug: e.target.value })} data-testid="insight-slug" /></Field>
            <Field label="Categoria">
              <select className="admin-input" value={editingInsight.category} onChange={(e) => setEditingInsight({ ...editingInsight, category: e.target.value })}>
                <option value="insight">insight</option>
                <option value="case-study">case-study</option>
                <option value="press">press</option>
              </select>
            </Field>
            <Field label="Titolo IT" full><input className="admin-input" value={editingInsight.title_it} onChange={(e) => setEditingInsight({ ...editingInsight, title_it: e.target.value })} data-testid="insight-title-it" /></Field>
            <Field label="Titolo EN" full><input className="admin-input" value={editingInsight.title_en} onChange={(e) => setEditingInsight({ ...editingInsight, title_en: e.target.value })} /></Field>
            <Field label="Excerpt IT" full><textarea rows={2} className="admin-input" value={editingInsight.excerpt_it} onChange={(e) => setEditingInsight({ ...editingInsight, excerpt_it: e.target.value })} /></Field>
            <Field label="Excerpt EN" full><textarea rows={2} className="admin-input" value={editingInsight.excerpt_en} onChange={(e) => setEditingInsight({ ...editingInsight, excerpt_en: e.target.value })} /></Field>
            <Field label="Body IT" full><textarea rows={8} className="admin-input" value={editingInsight.body_it} onChange={(e) => setEditingInsight({ ...editingInsight, body_it: e.target.value })} data-testid="insight-body-it" /></Field>
            <Field label="Body EN" full><textarea rows={6} className="admin-input" value={editingInsight.body_en} onChange={(e) => setEditingInsight({ ...editingInsight, body_en: e.target.value })} /></Field>
            <Field label="Tags (argomenti)" full>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map(tg => {
                  const active = (editingInsight.tags || []).includes(tg);
                  return (
                    <button key={tg} type="button" onClick={() => setEditingInsight({ ...editingInsight, tags: active ? editingInsight.tags.filter(t => t !== tg) : [...(editingInsight.tags || []), tg] })}
                      className="font-mono text-xs px-2 py-1"
                      style={{ border: `1px solid ${active ? "var(--midnight)" : "#E2E8F0"}`, background: active ? "var(--midnight)" : "transparent", color: active ? "var(--bone)" : "var(--midnight)", cursor: "pointer" }}>
                      {tg}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Region tags" full>
              <div className="flex flex-wrap gap-2">
                {REGION_OPTIONS.map(rg => {
                  const active = (editingInsight.region_tags || []).includes(rg);
                  return (
                    <button key={rg} type="button" onClick={() => setEditingInsight({ ...editingInsight, region_tags: active ? editingInsight.region_tags.filter(t => t !== rg) : [...(editingInsight.region_tags || []), rg] })}
                      className="font-mono text-xs px-2 py-1"
                      style={{ border: `1px solid ${active ? "var(--terracotta)" : "#E2E8F0"}`, background: active ? "var(--terracotta)" : "transparent", color: active ? "#fff" : "var(--midnight)", cursor: "pointer" }}>
                      {rg}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Minuti di lettura"><input type="number" min={3} max={20} className="admin-input" value={editingInsight.reading_minutes || 7} onChange={(e) => setEditingInsight({ ...editingInsight, reading_minutes: Number(e.target.value) })} /></Field>
            <Field label="Cover URL"><input className="admin-input" value={editingInsight.cover_url} onChange={(e) => setEditingInsight({ ...editingInsight, cover_url: e.target.value })} /></Field>
          </div>
        </Modal>
      )}

      {aiDraftForm && (
        <Modal title="Genera bozza articolo · GIANO Analytics" onClose={() => !generatingAi && setAiDraftForm(null)} onSave={generateAiDraft}>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Tema / topic *" full>
              <input
                className="admin-input"
                data-testid="ai-draft-topic"
                placeholder="es. Come si negozia un earn-out per una PMI meccanica"
                value={aiDraftForm.topic}
                onChange={(e) => setAiDraftForm({ ...aiDraftForm, topic: e.target.value })}
                autoFocus
                disabled={generatingAi}
              />
            </Field>
            <Field label="Angolazione / brief editoriale" full>
              <textarea
                rows={3}
                className="admin-input"
                data-testid="ai-draft-angle"
                placeholder="es. 5 errori tipici dell'imprenditore quando accetta un earn-out lungo. Cita numeri, cita casi tipici, chiudi con azioni concrete."
                value={aiDraftForm.angle}
                onChange={(e) => setAiDraftForm({ ...aiDraftForm, angle: e.target.value })}
                disabled={generatingAi}
              />
            </Field>
            <Field label="Argomenti (tags)" full>
              <div className="flex flex-wrap gap-2">
                {TAG_OPTIONS.map(tg => {
                  const active = aiDraftForm.tags.includes(tg);
                  return (
                    <button key={tg} type="button" disabled={generatingAi}
                      onClick={() => setAiDraftForm({ ...aiDraftForm, tags: active ? aiDraftForm.tags.filter(t => t !== tg) : [...aiDraftForm.tags, tg] })}
                      className="font-mono text-xs px-2 py-1"
                      style={{ border: `1px solid ${active ? "var(--midnight)" : "#E2E8F0"}`, background: active ? "var(--midnight)" : "transparent", color: active ? "var(--bone)" : "var(--midnight)", cursor: generatingAi ? "not-allowed" : "pointer" }}>
                      {tg}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Regioni target" full>
              <div className="flex flex-wrap gap-2">
                {REGION_OPTIONS.map(rg => {
                  const active = aiDraftForm.region_tags.includes(rg);
                  return (
                    <button key={rg} type="button" disabled={generatingAi}
                      onClick={() => setAiDraftForm({ ...aiDraftForm, region_tags: active ? aiDraftForm.region_tags.filter(t => t !== rg) : [...aiDraftForm.region_tags, rg] })}
                      className="font-mono text-xs px-2 py-1"
                      style={{ border: `1px solid ${active ? "var(--terracotta)" : "#E2E8F0"}`, background: active ? "var(--terracotta)" : "transparent", color: active ? "#fff" : "var(--midnight)", cursor: generatingAi ? "not-allowed" : "pointer" }}>
                      {rg}
                    </button>
                  );
                })}
              </div>
            </Field>
            <Field label="Categoria">
              <select className="admin-input" value={aiDraftForm.category} disabled={generatingAi} onChange={(e) => setAiDraftForm({ ...aiDraftForm, category: e.target.value })}>
                <option value="insight">insight</option>
                <option value="case-study">case-study</option>
                <option value="press">press</option>
              </select>
            </Field>
            <Field label="Lunghezza target (parole)">
              <input type="number" step={100} min={500} max={2000} className="admin-input" disabled={generatingAi}
                value={aiDraftForm.target_length_words}
                onChange={(e) => setAiDraftForm({ ...aiDraftForm, target_length_words: Number(e.target.value) })} />
            </Field>
            {generatingAi && (
              <div className="col-span-2 p-4" style={{ background: "#FDF6E3", border: "1px solid #E5D9B0" }}>
                <div className="flex items-center gap-3">
                  <Sparkles size={16} style={{ color: "var(--terracotta)" }} className="animate-pulse" />
                  <div className="text-sm">GIANO Analytics · Council AI sta scrivendo la bozza. Attendere 20-40 secondi.</div>
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="overline mb-2" style={{ color: "#64748B" }}>{label}</div>
      {children}
    </div>
  );
}

function Modal({ title, children, onClose, onSave }) {
  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }} onClick={onClose}>
      <div className="admin-card" style={{ background: "#fff", width: "100%", maxWidth: 780, maxHeight: "90vh", overflowY: "auto" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b" style={{ borderColor: "#E2E8F0" }}>
          <div className="serif-display" style={{ fontSize: 22 }}>{title}</div>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
        <div className="flex justify-end gap-3 p-5 border-t" style={{ borderColor: "#E2E8F0" }}>
          <button onClick={onClose} className="btn-ghost" style={{ padding: "0.5rem 1rem", fontSize: 13 }}>Annulla</button>
          <button onClick={onSave} className="btn-primary" style={{ padding: "0.5rem 1rem", fontSize: 13 }} data-testid="modal-save">Salva</button>
        </div>
      </div>
    </div>
  );
}
